import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UnpaidPaymentsRemoveModalComponent } from './components/unpaid-payments-remove-modal/unpaid-payments-remove-modal.component';
import { UnpaidPaymentsPaymentErrorModalComponent } from './components/unpaid-payments-payment-error-modal/unpaid-payments-payment-error-modal.component';
import { UnpaidPaymentsPaymentStatusModalComponent } from './components/unpaid-payments-payment-status-modal/unpaid-payments-payment-status-modal.component';
import { UnpaidPaymentsTransferModalComponent } from './components/unpaid-payments-transfer-modal/unpaid-payments-transfer-modal.component';
import { UnpaidPaymentsNotesModalComponent } from './components/unpaid-payments-notes-modal/unpaid-payments-notes-modal.component';
import { UnpaidPaymentsCalculationService } from './unpaid-payments-calculation.service';
import {
  UnpaidPaymentsConfirmDialogData,
  UnpaidPaymentsConfirmDialogResult,
  UnpaidPaymentsConfirmPaymentOpenConfig,
  UnpaidPaymentsConfirmPaymentModalResult,
  UnpaidPaymentsPaymentErrorModalOpenConfig,
  UnpaidPaymentsNotesModalData,
  UnpaidPaymentsNotesModalOpenConfig,
  UnpaidPaymentsPaymentStatusModalOpenConfig,
  UnpaidPaymentsRemoveModalOpenConfig,
  UnpaidPaymentsRemoveModalResult,
  UnpaidPaymentsTransferModalOpenConfig,
  UnpaidPaymentsTransferModalResult,
  UnpaidPaymentsUrgentNoteOpenConfig,
} from './unpaid-payments-modal.types';

const URGENT_NOTE_MARK_DEFAULTS = {
  title: 'Mark as urgent payment',
  message: 'Are you sure you want to mark this payment as urgent?',
  confirmLabel: 'Mark Urgent',
} as const;

const URGENT_NOTE_REMOVE_DEFAULTS = {
  title: 'Remove from urgent payment',
  message: 'Are you sure you want to remove this payment from urgent?',
  confirmLabel: 'Remove Urgent',
} as const;

@Injectable()
export class UnpaidPaymentsModalService {
  private readonly dialogOptions = {
    width: '520px',
    panelClass: 'confirmation-dialog-panel',
    autoFocus: 'first-tabbable' as const,
  };

  constructor(
    private readonly dialog: MatDialog,
    private readonly calculationService: UnpaidPaymentsCalculationService
  ) {}

  openConfirmPayment(
    config: UnpaidPaymentsConfirmPaymentOpenConfig
  ): Observable<UnpaidPaymentsConfirmPaymentModalResult | undefined> {
    const data: UnpaidPaymentsConfirmDialogData = {
      mode: 'confirm',
      title: config.title,
      totalAmount: config.totalAmount,
      paymentCount: config.paymentCount,
      confirmLabel: config.confirmLabel ?? 'Pay',
      cancelLabel: config.cancelLabel ?? 'Cancel',
      confirmDisabled: config.confirmDisabled,
      disabledMessage: config.disabledMessage,
    };

    return this.openConfirmDialog(data).pipe(
      map((result) => (result?.confirmed ? { confirmed: true } : undefined))
    );
  }

  /** Mark/remove urgent with shared copy, confirmation message, and textarea. */
  openUrgentRequiredNote(
    urgent: boolean,
    overrides: UnpaidPaymentsUrgentNoteOpenConfig = {}
  ): Observable<UnpaidPaymentsRemoveModalResult | undefined> {
    const defaults = urgent ? URGENT_NOTE_MARK_DEFAULTS : URGENT_NOTE_REMOVE_DEFAULTS;
    return this.openRequiredNote({
      title: defaults.title,
      message: defaults.message,
      confirmLabel: defaults.confirmLabel,
      ...overrides,
    });
  }

  /** Note-only confirm using the same unpaid dialog shell as remove. */
  openRequiredNote(config: {
    title: string;
    confirmLabel?: string;
    inputPlaceholder?: string;
    message?: string;
    useTextarea?: boolean;
  }): Observable<UnpaidPaymentsRemoveModalResult | undefined> {
    return this.openRemoveUnpaid({
      title: config.title,
      confirmLabel: config.confirmLabel ?? 'Confirm',
      inputPlaceholder: config.inputPlaceholder ?? 'Enter note',
      message: config.message,
      useTextarea: config.useTextarea ?? true,
    });
  }

  openRemoveUnpaid(
    config: UnpaidPaymentsRemoveModalOpenConfig
  ): Observable<UnpaidPaymentsRemoveModalResult | undefined> {
    const data: UnpaidPaymentsConfirmDialogData = {
      mode: 'remove',
      title: config.title ?? 'Are you sure you want to remove this payment from Unpaid ?',
      contractId: config.contractId,
      paymentCount: config.paymentCount,
      confirmLabel: config.confirmLabel ?? 'Remove',
      inputPlaceholder: config.inputPlaceholder ?? 'Enter note',
      message: config.message,
      useTextarea: config.useTextarea,
    };

    return this.openConfirmDialog(data).pipe(
      map((result) => {
        const note = result?.note?.trim();
        if (!result?.confirmed || !note) return undefined;
        return { confirmed: true, note };
      })
    );
  }

  openTransferFrom(
    config: UnpaidPaymentsTransferModalOpenConfig
  ): Observable<UnpaidPaymentsTransferModalResult | undefined> {
    return this.dialog
      .open(UnpaidPaymentsTransferModalComponent, {
        width: '520px',
        panelClass: 'confirmation-dialog-panel',
        autoFocus: 'first-tabbable',
        data: config,
      })
      .afterClosed();
  }

  openPaymentStatus(config: UnpaidPaymentsPaymentStatusModalOpenConfig): Observable<void> {
    return this.dialog
      .open(UnpaidPaymentsPaymentStatusModalComponent, {
        width: '480px',
        panelClass: 'confirmation-dialog-panel',
        data: config,
      })
      .afterClosed();
  }

  /** Unpaid v2 success UI — use instead of the global Confirmation modal for successes. */
  openActionSuccess(config: {
    title?: string;
    statusMessage: string;
    subtitle?: string;
    amount?: number;
    confirmLabel?: string;
  }): Observable<void> {
    return this.openPaymentStatus({
      title: config.title ?? 'Success',
      statusMessage: config.statusMessage,
      subtitle: config.subtitle,
      amount: config.amount,
      confirmLabel: config.confirmLabel ?? 'Done',
    });
  }

  /** Unpaid v2 error UI — use instead of the global Confirmation modal for errors. */
  openActionError(config: {
    title?: string;
    statusMessage: string;
    subtitle?: string;
    amount?: number;
    confirmLabel?: string;
  }): Observable<void> {
    return this.openPaymentError({
      title: config.title ?? 'Action Failed',
      statusMessage: config.statusMessage,
      subtitle: config.subtitle,
      amount: config.amount,
      confirmLabel: config.confirmLabel ?? 'Done',
    });
  }

  openPaymentError(config: UnpaidPaymentsPaymentErrorModalOpenConfig): Observable<void> {
    return this.dialog
      .open(UnpaidPaymentsPaymentErrorModalComponent, {
        width: '480px',
        panelClass: 'confirmation-dialog-panel',
        data: config,
      })
      .afterClosed();
  }

  /** Shared notes history modal for unpaid v2 child screens. */
  openNotes(config: UnpaidPaymentsNotesModalOpenConfig): Observable<void> {
    const entries =
      config.entries ?? this.calculationService.mapUnpaidUrgentNotesToEntries(Array.isArray(config.notes) ? config.notes : []);

    const data: UnpaidPaymentsNotesModalData = {
      title: config.title ?? 'Notes',
      emptyMessage: config.emptyMessage,
      entries,
    };

    return this.dialog
      .open(UnpaidPaymentsNotesModalComponent, {
        width: '720px',
        maxWidth: '95vw',
        panelClass: 'confirmation-dialog-panel',
        autoFocus: 'first-tabbable',
        data,
      })
      .afterClosed();
  }

  private openConfirmDialog(
    data: UnpaidPaymentsConfirmDialogData
  ): Observable<UnpaidPaymentsConfirmDialogResult | undefined> {
    return this.dialog
      .open(UnpaidPaymentsRemoveModalComponent, {
        ...this.dialogOptions,
        data,
      })
      .afterClosed();
  }
}
